import { Component } from 'react'
import { Field, reduxForm } from 'redux-form'
import * as styles from 'styles/molecules/forms/OnboardingForm.css'
import { ReduxFormProps, InputTheme } from 'interfaces/component'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { FormNameType } from 'interfaces/form'
import Button from 'components/features/pfm/Button/Button'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import CityStateField from 'components/atoms/inputs/CityStateField'
import { CreditFormValues, CreditExtendedFormValues } from 'interfaces/credit'
import { onFormSubmit } from 'helpers/formHelpers'
import { LocationOption } from 'interfaces/locations'
import {
  FIELD_ADDRESS1_PROPS,
  FIELD_ADDRESS2_PROPS,
  FIELD_BIRTH_DATE_PROPS,
  FIELD_FIRST_NAME_PROPS,
  FIELD_LAST_NAME_PROPS,
  FIELD_ZIP_PROPS,
} from 'constants/fields'

export type CreditFormStateProps = {
  form: FormNameType
  isExtended: boolean
  initialValues: CreditFormValues | CreditExtendedFormValues
  asyncValidate: (values?: CreditFormValues | CreditExtendedFormValues) => Promise<{}>
  onSubmit: typeof onFormSubmit
  isSubmitAllowed: boolean
  cityStateValue: LocationOption | null
}

export type CreditFormOwnProps = {
  theme?: InputTheme
  isDisabled: boolean
}

type CreditFormProps = ReduxFormProps & CreditFormStateProps & CreditFormOwnProps & {
  initialValues: CreditFormValues | CreditExtendedFormValues
}


const SSN4_PROPS = {
  label: 'SSN (Last 4 Digits)',
  placeholder: '1234',
  maxLength: 4,
  qaMarker: 'ssn4-input',
}

const SSN9_PROPS = {
  label: 'SSN (All 9 Digits)',
  placeholder: '123456789',
  maxLength: 9,
  qaMarker: 'ssn-input',
}

const renderSSNField = (isExtended: boolean, disabled: boolean) => {
  const commonSSNProps = {
    type: 'password',
    autoComplete: 'new-password',
    autoFocus: !disabled,
    disabled,
    pattern: '[0-9]*',
    hint: 'We protect your data with 256-bit encryption. We will never sell it.',
    hintIcon: 'secure',
  }

  const props = {
    ...commonSSNProps,
    ...(isExtended ? SSN9_PROPS : SSN4_PROPS),
  }

  const name = isExtended ? 'ssn' : 'ssn4'

  return (
    <Field
      name={ name }
      props={ props }
      component={ TextFieldWrapped }
      normalize={ (value: string) => value.replace(/\D/g, '') }
    />
  )
}

@reduxForm({
  shouldAsyncValidate,
  destroyOnUnmount: false,
})
class CreditForm extends Component<CreditFormProps> {

  render () {
    const {
      isExtended, isDisabled,
      handleSubmit,
      isSubmitAllowed,
      theme,
    } = this.props

    const defaultButtonProps = getSaveButtonFormProps(this.props)
    const buttonProps = {
      ...defaultButtonProps,
      disabled: defaultButtonProps.disabled || !isSubmitAllowed,
    }

    return (
      <form className={ styles.onboardingForm } onSubmit={ handleSubmit }>
        <ul className={ styles.controlList }>
          { isExtended && (
            <li className={ styles.controlListItem }>
              <Field
                name='first_name'
                props={{
                  ...FIELD_FIRST_NAME_PROPS,
                  disabled: isDisabled,
                }}
                component={ TextFieldWrapped }
              />
            </li>
          )}
          { isExtended && (
            <li className={ styles.controlListItem }>
              <Field
                name='last_name'
                props={{
                  ...FIELD_LAST_NAME_PROPS,
                  disabled: isDisabled,
                }}
                component={ TextFieldWrapped }
              />
            </li>
          )}
          { isExtended && (
            <li className={ styles.controlListItem }>
              <Field
                name='birth_date'
                props={{
                  ...FIELD_BIRTH_DATE_PROPS,
                  disabled: isDisabled,
                }}
                component={ TextFieldWrapped }
              />
            </li>
          )}
          <li className={ styles.controlListItem }>
            { renderSSNField(isExtended, isDisabled) }
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name='address1'
              props={{
                ...FIELD_ADDRESS1_PROPS,
                disabled: isDisabled,
              }}
              component={ TextFieldWrapped }
            />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name='address2'
              props={{
                ...FIELD_ADDRESS2_PROPS,
                disabled: isDisabled,
              }}
              component={ TextFieldWrapped }
            />
          </li>
          <li className={ styles.controlListItem }>
            <CityStateField
              name='city'
              theme={ theme }
              disabled={ isDisabled }
            />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name='zip5'
              props={{
                ...FIELD_ZIP_PROPS,
                qaMarker: isExtended ? 'zip5-input' : 'zip-code-input',
                disabled: isDisabled,
              }}
              component={ TextFieldWrapped }
            />
          </li>
          { !isDisabled && (
            <li className={ styles.submitListItem }>
              <div className={ styles.buttonContainer }>
                <Button { ...buttonProps } submit fullWidth qaMarker='submit-button'>
                  Link Your Credit File
                </Button>
              </div>
            </li>
          )}
        </ul>
      </form>
    )
  }

  componentDidMount () {
    // it is needed for initial disabling submit button
    this.props.asyncValidate()
  }

  componentDidUpdate (prevProps: CreditFormProps) {
    const nextCityStateValue = this.props.cityStateValue
    const prevCityStateValue = prevProps.cityStateValue

    if (nextCityStateValue !== prevCityStateValue) {
      this.props.asyncValidate()
    }
  }
}

export default CreditForm
